/**
 *  AenaPoints script file
**/
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { GETTERS, ACTIONS, MUTATIONS } from '@/store'
import { AenaSearch } from './aena-search'
import Searcher from '@/components/searcher/Searcher.vue'
import { AenaHighlightedPoints } from './aena-highlighted-points'
import { AenaPointsList } from './aena-points-list'
import { AenaPointsCategories } from './aena-points-categories'
import { AenaPointDetail } from './aena-point-detail'
import { AenaRoute } from './aena-route'
import saveActions from '@/mixins/saveActions'
import capitalize from '@/filters/capitalize'
import durationTime from '@/filters/durationTime'
import getRoutePoint from '@/mixins/getRoutePoint'
import { searchWord, normalize } from '@/utils/searchWord'
import draggable from 'vuedraggable'
import displayCoordinates from '@/filters/displayCoordinates'
import { getElementRef } from '@/utils/getElementRef'
import { normalizeLowerCase } from '@/utils/matchString'
import setDeviceWidthClass from '@/mixins/setDeviceWidthClass'
import { getEventLabelGM } from '@/utils/getEventLabelGM'
import { toggleFocus } from '@/utils/toggleFocus'

const userAgent = navigator.userAgent || navigator.vendor || window.opera

export default {
  name: 'AenaPoints',
  mixins: [saveActions, durationTime, capitalize, getRoutePoint, displayCoordinates, setDeviceWidthClass],
  components: {
    draggable,
    AenaSearch,
    Searcher,
    'aena-highlighted-points': AenaHighlightedPoints,
    'aena-points-list': AenaPointsList,
    'aena-points-categories': AenaPointsCategories,
    'aena-point-detail': AenaPointDetail,
    'aena-route': AenaRoute
  },
  props: {
    search: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      view: 'aena-highlighted-points',
      lastView: '',
      currentProps: {
        highlighted: [],
        points: [],
        airport: undefined,
        category: undefined,
        categories: [],
        filters: [],
        activeFilters: {
          categories: null,
          subcategories: [],
          building: undefined,
          search: undefined,
          associatedParking: []
        },
        search: false,
        headerHeight: 0
      },
      blockedPois: false,
      toggleDrag: false,
      searcherFocus: false,
      memoryValue: undefined,
      isVisible: false,
      appBanner: false,
      isCollapsed: false,
      pmr_enabled: process.env.VUE_APP_PMR_MODE
    }
  },
  created () {
    if (this.category || this.subcategories) {
      if (this.category) {
        const cat = this.poisCatalog.find(x => x.id === this.category)
        this.onSelect({ component: 'aena-points-categories', category: cat })
      } else {
        this.onSelect({ component: 'aena-points-categories', subcategories: this.subcategories })
      }
    } else {
      this.setHigtLight(this.poisCatalog)
    }

    if (this.poi) {
      this.setPoi(this.poi)
    }

    if (this.routeConfig) {
      this[ACTIONS.LOAD_ROUTE]({
        routeMode: true
      })
      this.setDestination()
      this.toggleBlocked()
    }
  },
  updated () {
    if (this.$refs.header && (this.$refs.header.offsetHeight !== this.currentProps.headerHeight)) {
      this.currentProps.headerHeight = this.$refs.header.offsetHeight
    }

    if (this.$refs.time) {
      this.$refs.time.focus()
    }
  },
  computed: {
    ...mapGetters({
      isMupiMode: GETTERS.IS_MUPI_MODE,
      isMobile: GETTERS.GET_IS_MOBILE,
      explorerMode: GETTERS.GET_EXPLORERMODE,
      airport: GETTERS.GET_AIRPORT,
      airportID: GETTERS.GET_AIRPORT_ID,
      category: GETTERS.GET_CATEGORY,
      subcategories: GETTERS.GET_SUBCATEGORIES,
      poisCatalog: GETTERS.GET_POIS_CATALOG,
      activeFiltersC: GETTERS.GET_ACTIVE_FILTERS,
      poisList: GETTERS.GET_POIS_LIST,
      poi: GETTERS.GET_POI,
      poiId: GETTERS.GET_POI_ID,
      poiDId: GETTERS.GET_POID_ID,
      buildings: GETTERS.GET_BUILDINGS,
      isRouteMode: GETTERS.IS_ROUTE_MODE,
      stepsAdded: GETTERS.GET_NEW_POINT,
      hasNewPoint: GETTERS.HAS_NEW_POINT,
      getView: GETTERS.GET_VIEW,
      routeConfig: GETTERS.GET_ROUTE_CONFIG,
      pmrProfile: GETTERS.GET_PMR_PROFILE,
      pmrDefaultProfile: GETTERS.GET_DEFAULT_PMR_PROFILE
    }),
    currentCategory () {
      return !!this.currentProps.category && this.poisCatalog.find(c => c.id === this.currentProps.category.id)
    },
    addBlocked () {
      return !(this.origin && this.destination) || this.hasNewPoint
    },
    pmrActive () {
      return this.pmrProfile !== null
    },
    isAndroid () {
      return /android/i.test(userAgent)
    },
    isIOS () {
      return (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
    },
    appLink () {
      let link = ''

      if (this.isAndroid) {
        link = 'https://play.google.com/store/apps/details?id=es.aena.mobile'
      }

      if (this.isIOS) {
        link = 'https://apps.apple.com/es/app/aena/id686440022'
      }

      return link
    }
  },
  methods: {
    ...mapActions([
      ACTIONS.SET_FEEDBACK,
      ACTIONS.LOAD_ACTIVE_FILTERS,
      ACTIONS.LOAD_VIEW,
      ACTIONS.REMOVE_STEP,
      ACTIONS.SET_PMR_PROFILE
    ]),
    ...mapMutations([
      MUTATIONS.SAVE_PMR_CONFIG_VISIBILITY
    ]),
    resetActiveFilters () {
      this.currentProps.category = undefined
      this.currentProps.activeFilters = {
        subcategories: [],
        categories: null,
        building: this.currentProps.activeFilters.building,
        associatedParking: this.findAssociatedParkings(this.currentProps.activeFilters.building),
        search: undefined
      }
      this.saveActiveFilters()
    },
    getActiveFilters () {
      this.currentProps.activeFilters = this.activeFiltersC
    },
    saveActiveFilters () {
      this[ACTIONS.LOAD_ACTIVE_FILTERS](this.currentProps.activeFilters)
    },
    onInputFocus () {
      this.searcherFocus = true
      this.isVisible = true
    },
    onInputBlur () {
      this.searcherFocus = false
    },
    onInputChange ({ value, name } = {}) {
      this.setDestination(name)
      this.currentProps.category = undefined
      this.currentProps.activeFilters.subcategories = []
      this.currentProps.activeFilters.categories = null
      this.currentProps.activeFilters.search = value
      this.currentProps.search = !!value
      this.saveActiveFilters()
      if (!value) {
        this.view = 'aena-points-list'
        this.onSelect({ component: 'aena-points-list' })
      } else {
        this.onSelect({ component: 'aena-points-categories', search: value })
      }
    },
    onInputEnterKey () {
      this.$refs.component.$refs.section.focus()
    },
    goBack () {
      this.isVisible = !this.isVisible
    },
    async onSelect ({ category, component, pointDetail, search, pointGrouped, subcategories }) {
      switch (component) {
        case 'aena-highlighted-points':
          this.resetActiveFilters()
          this.setHigtLight(this.poisCatalog)
          break
        case 'aena-points-list':
          this.resetActiveFilters()
          this.setCategories(this.poisCatalog)
          break
        case 'aena-points-categories':
          this.getActiveFilters()
          const hasCategory = category || this.currentProps.category
          const hasSubcategories = (subcategories && subcategories.length) || this.currentProps.activeFilters.subcategories.length
          const hasBuilding = this.currentProps.activeFilters.building
          const hasSearch = this.currentProps.activeFilters.search || search

          if (!hasCategory && !hasSubcategories && !hasBuilding && !hasSearch) {
            this.onSelect({ component: this.lastView })
            return
          }
          this.currentProps.activeFilters.categories = category || this.currentProps.activeFilters.categories
          this.currentProps.activeFilters.subcategories = subcategories || this.currentProps.activeFilters.subcategories
          let cat = category || this.currentProps.category

          if (!cat && this.currentProps.activeFilters.subcategories) {
            cat = this.poisCatalog.find(x => x.subcategories.some(y => y.id === this.currentProps.activeFilters.subcategories[0]))
          }

          this.setPoisCategories({ category: cat, search: search || this.currentProps.activeFilters.search })
          break
        case 'aena-point-detail':
          this.saveActiveFilters()
          this.savePoi({ poiId: pointDetail.id, pointGrouped, airportID: this.airportID, tag: 'list' })
          break
        case 'aena-route-pre':
          this.memoryValue = undefined

          if (this.view === 'aena-point-detail') {
            this.onSelect({ component: 'aena-highlighted-points' })
          }

          let key = 'destination'
          if (this.isMupiMode && this.poiId === pointDetail.properties.id) {
            key = 'origin'
          }

          if (this.isMupiMode && this.poiDId === pointDetail.properties.id) {
            key = 'destination'
          }

          this[ACTIONS.LOAD_ROUTE]({
            i18n: this.$i18n,
            [key]: pointDetail,
            routeMode: true
          })

          return
        case 'aena-route-poi':
          this.memoryValue = undefined

          const destinationName = (this.destinationName || '').includes('step') ? 'step' : this.destinationName
          const name = destinationName || (this.origin ? 'destination' : 'origin')

          this.onSelect({ component: 'aena-highlighted-points' })
          await this[ACTIONS.LOAD_ROUTE]({
            i18n: this.$i18n,
            [name]: pointDetail,
            routeMode: true
          })

          this.focus()

          return
      }

      if (component !== this.view) {
        this.lastView = this.view
      }

      this.view = component

      if (this.isMobile && this.view === 'aena-point-detail') {
        this.$refs.search.active = false
        this.$refs.search.focus = false
        this.isVisible = true
      }

      if (this.isMobile && (this.lastView === 'aena-point-detail' || this.lastView === 'aena-route') && this.view === 'aena-highlighted-points') {
        this.$nextTick(() => {
          this.$refs.search.active = false
          this.$refs.search.focus = false
          this.isVisible = false
        })
      }
    },
    onFilter (filter) {
      this.currentProps.activeFilters.building = filter
      this.currentProps.activeFilters.associatedParking = this.findAssociatedParkings(filter)
      this.setPois(this.currentProps.activeFilters.search)
    },
    onCategory ({ data, type }) {
      let _data = data
      this.currentProps.activeFilters.categories = null
      if (type === 'category') {
        this.currentProps.activeFilters.categories = data
        _data = []
      }

      this.onSubcategory(_data, this.currentProps.activeFilters.search)
    },
    onSubcategory (categories = [], search) {
      this.currentProps.activeFilters.categories = this.currentProps.activeFilters.categories
      this.currentProps.activeFilters.subcategories = categories
      this.setPois(search)
    },
    setFilterCategories (search) {
      const localSearch = search || this.currentProps.activeFilters.search
      if (localSearch) {
        const allSubcategories = this.poisCatalog.map(x => x.subcategories).flat()
        const array = this.currentProps.points.reduce((acum, current) => {
          const subcategories = current.properties.subcategories
          const subcategoriesChecked = subcategories.reduce((a, c) => {
            const subcategory = allSubcategories.find(s => s.id === c)
            const regex = new RegExp(normalize(localSearch), 'gi')
            if (subcategory.name.match(regex)) {
              a.push(c)
            }
            return a
          }, [])

          if (!subcategoriesChecked.length) {
            acum.push(...subcategories)
          } else {
            acum.push(...subcategoriesChecked)
          }

          return acum
        }, [])

        this.currentProps.categories = [...new Set(array)]
          .reduce((acum, current) => {
            const el = allSubcategories.find(s => s.id === current)
            if (el) {
              acum.push(el)
            }
            return acum
          }, [])
      } else {
        if (this.currentCategory) {
          this.currentProps.categories = this.poisCatalog.find(x => x.id === this.currentCategory.id).subcategories
        } else {
          const a = this.poisCatalog.map(x => x.subcategories).flat()
          this.currentProps.categories = [...new Set(a.map(x => x.id))]
            .map(id => {
              return a.find(a => a.id === id)
            })
        }
      }
    },
    setHigtLight (value = []) {
      this.currentProps.highlighted = value.filter(e => e.featured).slice(0, 6)
        .sort((a, b) => { return a.featured_order - b.featured_order })
    },
    setCategories (value) {
      this.currentProps.points = value
    },
    setPois (search) {
      if (search && search !== this.currentProps.activeFilters.search) {
        this.currentProps.activeFilters.subcategories = []
      }
      const searchSubcategories = search ? this.poisCatalog.reduce((acum, current) => {
        acum = acum.concat(current.subcategories.filter(s => this.findWord(s.name, search)))
        return acum
      }, []).map(x => x.id) : []
      this.currentProps.points = this.poisList.features.filter(x => {
        return (
          this.findSubcategoriesInPois(x) &&
          this.findPoisInSubcategoriesActives(x) &&
          this.findPoisInBuildingActives(x) &&
          this.findPoisInSearchActives(x, search, searchSubcategories)
        )
      }).sort(this.orderPois)
    },
    orderPois (poi, poi2) {
      if (poi.properties.building > poi2.properties.building) {
        return 1
      }
      if (poi.properties.building < poi2.properties.building) {
        return -1
      }
      if (poi.properties.building === poi2.properties.building) {
        if (poi.properties.level > poi2.properties.level) {
          return 1
        }
        if (poi.properties.level < poi2.properties.level) {
          return -1
        }
        if (poi.properties.level === poi2.properties.level) {
          if (poi.properties.attrs.pat_nombre > poi2.properties.attrs.pat_nombre) {
            return 1
          }
          if (poi.properties.attrs.pat_nombre < poi2.properties.attrs.pat_nombre) {
            return -1
          }
        }
      }
      return 0
    },
    findSubcategoriesInPois (x) {
      return !this.currentProps.category || x.properties.subcategories.some(x => this.currentCategory.subcategories.some(s => s.id === x))
    },
    findPoisInSubcategoriesActives (x) {
      return !this.currentProps.activeFilters.subcategories.length || x.properties.subcategories.some(x => this.currentProps.activeFilters.subcategories.includes(x))
    },
    findPoisInBuildingActives (x) {
      return !this.currentProps.activeFilters.building || x.properties.building === this.currentProps.activeFilters.building || this.currentProps.activeFilters.associatedParking.some(y => y === x.properties.building)
    },
    findPoisInSearchActives (x, search, searchSubcategories) {
      return !search || this.findWord(x.properties.attrs.pat_nombre, search) || this.findSubcategoryWord(x.properties.subcategories, searchSubcategories)
    },
    findWord (data = '', search) {
      if (!data) {
        return false
      }
      return searchWord(search, data)
    },
    findSubcategoryWord (data = [], subcategories) {
      if (!data.length) {
        return false
      }
      return data.some(x => subcategories.includes(x))
    },
    findAssociatedParkings (id) {
      const el = this.buildings.find(x => x.id === id)
      if (el) {
        return el.poi_search_group_ids
      }

      return []
    },
    setPoisCategories ({ category, search }) {
      this.currentProps.airport = this.airport
      this.currentProps.category = category
      this.currentProps.filters = this.buildings
      this.onSubcategory(this.currentProps.activeFilters.subcategories, search)
      this.setFilterCategories(search)
    },
    setPoi (poi) {
      this.onSelect({
        component: 'aena-point-detail',
        pointDetail: {
          id: poi.properties.id
        }
      })
    },
    closeRoute () {
      this[ACTIONS.CLEAR_ROUTE]()
      this[ACTIONS.SET_FEEDBACK]()
      this.blockedPois = false
      this.memoryValue = undefined
      const view = this.view === 'aena-route' ? 'aena-points-categories' : this.view

      this.onSelect({ component: view, category: this.currentProps.category })

      this.$nextTick(() => {
        if (this.isMobile && view === 'aena-highlighted-points') {
          this.$refs.container.classList.remove('visible')
          this.isVisible = false
        }
      })
    },
    resetView () {
      this.onSelect({ component: 'aena-highlighted-points' })
    },
    clearStep (index, ref, hold) {
      if (this.explorerMode) {
        this.currentSearch = undefined
        const unlockSpecialSteps = this.steps.length > 2 && !ref
        const step = this.steps[index]
        const clear = !step || !step.new || !hold

        if (this.memoryValue) {
          if (this.destinationName === 'origin') {
            index++
          }
          this[ACTIONS.LOAD_ROUTE]({
            i18n: this.$i18n,
            [this.destinationName]: this.memoryValue,
            reset: true
          })
        }

        this.memoryValue = undefined
        this.memoryValue = (step && !step.new) ? step : undefined
        this.blockedPois = false
        this.resetView()
        this.setDestination(ref)
        this.focus(ref)
        if (clear) {
          const tempSteps = [...this.steps]
          this[ACTIONS.REMOVE_STEP]({ index, hold })

          if (unlockSpecialSteps) {
            let newIndex = index === tempSteps.length - 1 ? index - 1 : index + 1

            this[ACTIONS.LOAD_ROUTE]({
              i18n: this.$i18n,
              [this.destinationName]: tempSteps[newIndex],
              reset: true
            })
            this[ACTIONS.REMOVE_STEP]({ index: newIndex })
          }
        }

        const el = getElementRef(this.$refs[ref])
        if (this.memoryValue && this.memoryValue.mode !== 'point' && el) {
          el.inputValue = this.$t(this.memoryValue.properties.attrs.pat_nombre)
          el.onInputChange()
        }
      }
    },
    addStep () {
      this.blockedPois = false
      if (!this.addBlocked) {
        this.setDestination('step' + this.stepsAdded.length)
        this.resetView()
        this[ACTIONS.LOAD_ROUTE]({
          i18n: this.$i18n,
          reset: true,
          routeMode: true,
          step: {
            new: true
          }
        })
      }
    },
    toggleBlocked () {
      if (!this.blockedPois) {
        this.onSelect({ component: 'aena-route' })

        if (this.origin && this.destination) {
          const origin = getEventLabelGM(this.origin, this.$i18n)
          const destination = getEventLabelGM(this.destination, this.$i18n)

          const tag = {
            'eventCategory': 'como llegar',
            'eventAction': 'clic en ruta',
            'eventLabel': `${normalizeLowerCase(origin)} - ${normalizeLowerCase(destination)}`,
            'language': this.$i18n.locale,
            'airportID': this.airportID
          }
          window.digitalChange('event', tag)
        }
      }
      this.blockedPois = !this.blockedPois
    },
    focus (ref) {
      const destinationName = ref || this.destinationName
      if (destinationName) {
        const el = getElementRef(this.$refs[destinationName])
        el.onFocus()
      }
    },
    onDrag (event) {
      this.toggleDrag = !this.toggleDrag
    },
    onTransitionEnter () {
      if (!this.searcherFocus) {
        this.$refs.component.$refs.section.focus()
      }
    },
    toggleBanner () {
      this.appBanner = !this.appBanner
    },
    toggleCollapsed (value) {
      this.isCollapsed = value
      this.$emit('toggle', value)
    },
    showPMRConfig () {
      this[MUTATIONS.SAVE_PMR_CONFIG_VISIBILITY](true)

      window.setTimeout(() => {
        const parent = document.querySelector('.flughaveno-map')
        const popup = document.querySelector('.pmr-popup')
        toggleFocus('pmr-wrapper', parent, true)
        popup.focus()
      }, 300)
    },
    togglePMR () {
      this[ACTIONS.SET_PMR_PROFILE](this.pmrProfile ? null : this.pmrDefaultProfile)
    }
  },
  watch: {
    poisCatalog (value) {
      this.setHigtLight(value)
    },
    poi (value, prev) {
      if (!prev || (value && value.properties.id !== prev.properties.id)) {
        this.setPoi(value)
      }
    },
    origin (value, prev) {
      if (!value && prev) {
        this.$refs.origin.onFocus()
      }
    },
    destination (value, prev) {
      if (!value && prev) {
        this.$refs.destination.onFocus()
      }
    },
    getView (value) {
      if (value && this.view === 'aena-point-detail') {
        this.onSelect({ component: value })
      }

      this[ACTIONS.LOAD_VIEW]()
    },
    poisList (value) {
      this.setPois()
    },
    isRouteMode (value) {
      if (!value) {
        this.onSelect({ component: 'aena-highlighted-points' })
      }
    }
  }
}
